import React from "react";
import styled from "styled-components";
import { customElement } from "../../marcel/customElement";
import MrclText from "../../marcel/MrclText";
import DeltaBadge from "../../DeltaBadge";
import { COLORS } from "../../../utils/colors";
import { formatKvalue } from "../../../utils/numberUtils";

export interface TerritoryData {
  territoryName: string;
  storeCount: number;
  overallTrend: number;
  caN: number;
  progCa: number;
  ecartBudget: number;
}

interface TerritoryDataHeaderProps {
  data: TerritoryData;
  onBack: () => void;
}

const formatPercentage = (val: number, showSign = true): string => {
  const formatted = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
  const sign = showSign && val > 0 ? "+" : "";
  return `${sign}${formatted}\u00A0%`;
};

export const TerritoryDataHeader: React.FC<TerritoryDataHeaderProps> = ({ data, onBack }) => {
  const { territoryName, storeCount, overallTrend, caN, progCa, ecartBudget } = data;

  const isEcartPositive = ecartBudget >= 100;

  return (
    <HeaderContainer>
      <TopSection>
        <IdentityArea>
          <BackButton
            icon="chevron-left"
            variant="transparent"
            tone="neutral"
            size="s"
            onClick={onBack}
            aria-label="Retour"
          />
          <TitleWrapper>
            <TerritoryName>{territoryName}</TerritoryName>
            <StoreCount>
              {storeCount} {storeCount > 1 ? "magasins" : "magasin"}
            </StoreCount>
          </TitleWrapper>
        </IdentityArea>
        <TrendArea>
          <DeltaBadge value={overallTrend} showSignWithIcon />
        </TrendArea>
      </TopSection>

      <KpiRow>
        <KpiItem>
          <KpiLabel>CA N</KpiLabel>
          <KpiValue>{formatKvalue(caN)}</KpiValue>
        </KpiItem>
        <KpiItem>
          <KpiLabel>VAR N-1</KpiLabel>
          <ColoredValue $isPositive={progCa >= 0}>
            {formatPercentage(progCa)}
          </ColoredValue>
        </KpiItem>
        <KpiItem>
          <KpiLabel>ECART BUDGET</KpiLabel>
          <ColoredValue $isPositive={isEcartPositive}>
            {formatKvalue(ecartBudget)}
          </ColoredValue>
        </KpiItem>
      </KpiRow>
    </HeaderContainer>
  );
};

export default TerritoryDataHeader;

// Styled Components
const HeaderContainer = styled.div`
  background: #ffffff;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const IdentityArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// The inner shadow button is sized from --mrcl-sizing-3xl (size "s") and
// coloured from Marcel tokens: match it to the space left inside the 1px
// border so the icon stays centred.
const BackButton = styled(customElement("mrcl-icon-button"))`
  --mrcl-sizing-3xl: 38px;
  --mrcl-action-content-neutral-default: #005ca9;
  --mrcl-action-content-neutral-hover: #005ca9;
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  border: 1px solid #005ca9;
  border-radius: 4px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const TerritoryName = styled(MrclText)`
  font-family: Ubuntu, sans-serif;
  font-size: 14px !important;
  font-weight: 700;
  color: #121212;
`;

const StoreCount = styled(MrclText)`
  font-family: Ubuntu, sans-serif;
  font-size: 14px !important;
  color: #696969;
`;

const TrendArea = styled.div`
  display: flex;
  align-items: center;
`;

const KpiRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid #ebebeb;
  width: 100%;
`;

const KpiItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const KpiLabel = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  color: #696969;
  font-weight: 500;
`;

const KpiValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #121212;
`;

const ColoredValue = styled.span<{ $isPositive: boolean }>`
  font-size: 16px;
  font-weight: 700;
  color: ${({ $isPositive }) =>
    $isPositive ? COLORS?.green || "#00875A" : COLORS?.red || "#DE350B"};
`;
